import { MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { Note } from "@/Common/Models/Note";
import { Patient } from "@/Common/Models/Patient/Patient";
import { DocumentUIState } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { AttachmentsTooltipControl } from "@/Documents/Components/Attachments/AttachmentsTooltipControl";
import { DocumentCreate } from "@/Documents/Components/DocumentCreate/DocumentCreate";
import { DocumentInAppView, DocumentNavigation } from "@/Documents/Components/DocumentInAppView/DocumentInAppView";
import { DocumentNotesPopup } from "@/Documents/Components/DocumentNotes/DocumentNotesPopup";
import { DocumentTreeControl } from "@/Documents/Components/DocumentTreeControl/DocumentTreeControl";
import { AttachmentSortingHelper } from "@/Documents/Helpers/AttachmentSortingHelper";
import { IDocumentsTabModel } from "@/Documents/Models/UI/IDocumentsTabModel";
import { PatientDocumentEdit } from "./PatientDocumentEdit";
import { PatientDocumentSearch } from "./PatientDocumentSearch";
import { PatientDocumentView } from "./PatientDocumentView";

interface IPatientDocuments {
    patientId: string;
    patient: Patient;
}

class PatientDocumentsState {
    public Model: IDocumentsTabModel;
    public ColumnManagerModel: Immutable.List<ColumnConfigurationModel>;
    public TableColumnInfo: Immutable.List<MyleTableColumnOptions>;
    public TableRenderOptions: ITableRenderOptions;
}

class PatientDocuments extends React.Component<IPatientDocuments, PatientDocumentsState> {
    private changeListenerReference: string;

    constructor(props: IPatientDocuments) {
        super(props);

        // Restore and init state from cache
        documentsStore.ImportDocumentsModel(patientStore.PatientDocuments[this.props.patientId]);
        documentsStore.InitializeForGlobalDocuments(true, true);

        const newState = new PatientDocumentsState();
        newState.Model = documentsStore.DocumentsModel;
        newState.ColumnManagerModel = documentsStore.ColumnManagerModel;
        this.state = newState;
    }

    public componentDidMount(): void {
        this.changeListenerReference = documentsStore.AddChangeListener(this.changeListener, this, "patient-docs");
        $(window).trigger("resize");

        if (this.state.Model.get("UIState") === DocumentUIState.Search) {
            documentsStore.SearchDocuments();
        }
    }

    public componentWillUnmount(): void {
        documentsStore.SaveState();
        documentsStore.RemoveChangeListener(this.changeListenerReference);
    }

    public componentDidUpdate(prevProps: Readonly<IPatientDocuments>): void {
        if (this.props !== prevProps) {
            if (this.props.patientId != prevProps.patientId) {
                this.ResetDocumentModel(this.props.patientId);
                documentsStore.SearchDocuments();
            }
        }
    }

    public render(): JSX.Element {
        let viewToReturn: JSX.Element;
        let noteComponent: JSX.Element = null;

        if (this.state.Model.get("NoteWindowOpen")) {
            noteComponent = (
                <DocumentNotesPopup
                    controlId={this.state.Model.get("NotesDropdownControlId")}
                    notes={this.state.Model.get("CurrentDocumentNotes")
                        .map(y => {
                            var note = new Note();
                            note.ContactId = y.ContactId;
                            note.NoteContent = y.NoteContent;
                            note.NoteDate = y.NoteDate;
                            note.NoteId = y.NoteId;
                            return note;
                        })
                        .toArray()
                    }
                    referenceId={this.state.Model.get("CurrentDocumentIDForNotes")}
                    eventToDispatchOnDropdownClosed={LocalOperations.Documents.Note_Dropdown_Closed}
                    model={this.state.Model.get("SelectedDocument")}
                />
            );
        }

        switch (this.state.Model.get("UIState")) {
            case DocumentUIState.Search:
                viewToReturn = (
                    <PatientDocumentSearch
                        Model={this.state.Model}
                        ColumnManagerModel={this.state.ColumnManagerModel}
                        patient={this.props.patient}
                    />
                );
                break;

            case DocumentUIState.Edit:
                viewToReturn = (
                    <PatientDocumentEdit
                        model={this.state.Model.get("SelectedDocument")}
                        documentClasses={this.state.Model.get("DocumentClasses").toList()}
                        sources={this.state.Model.get("DocumentSources")}
                        descriptions={this.state.Model.get("DocumentDescription")}
                        beginUpload={this.state.Model.get("BeginUpload")}
                        sites={this.state.Model.get("Sites")}
                    />
                );
                break;

            case DocumentUIState.Create:
                viewToReturn = (
                    <DocumentCreate
                        model={this.state.Model.get("NewDocument")}
                        documentClasses={this.state.Model.get("DocumentClasses").toList()}
                        sources={this.state.Model.get("DocumentSources")}
                        docDescription={this.state.Model.get("DocumentDescription")}
                        beginUpload={this.state.Model.get("BeginUpload")}
                        sites={this.state.Model.get("Sites")}
                        isDocumentCreateFromPatient={true}
                    />
                );
                break;

            case DocumentUIState.View:
                viewToReturn = (
                    <PatientDocumentView
                        model={this.state.Model.get("SelectedDocument")}
                        documentClasses={this.state.Model.get("DocumentClasses").toList()}
                        sources={this.state.Model.get("DocumentSources")}
                        descriptions={this.state.Model.get("DocumentDescription")}
                        documentTabModel={this.state.Model}
                    />
                );
                break;

            case DocumentUIState.Annotate:
                this.sortDocumentAttachments(documentsStore.DocumentsModel.get("SelectedDocument").get("MedicalDocumentInfo").Model);

                viewToReturn = (
                    <DocumentInAppView
                        document={documentsStore.DocumentsModel.get("SelectedDocument")}
                        documentIndex={this.getDocumentIndex()}
                        documentCount={this.getDocumentCount()}
                        isSendToPortalPopupOpen={this.state.Model.get("IsSendToPatientPortalPopupOpen")}
                        onDocumentNavigation={navigation => this.onDocumentNavigation(navigation)}
                        isPatientContext={true}
                        portalLinkedItem={this.state.Model.get("PortalLinkedItemModel")}
                    />
                );
                break;

            default:
                throw new TypeError("undefined view state");
        }

        var leftMenuStateCss = this.state.Model.get("LeftMenuOpen") ? "left-nav-menu documents responsive transition" : "left-nav-menu documents transition closed";
        var leftMenuControlsCss = this.state.Model.get("LeftMenuOpen") ? "left-nav-menu-handle responsive" : "left-nav-menu-handle";
        return (
            <div className="inner-container inherit-height">
                <div className="tb patient-docs embedded inherit-height">
                    {this.state.Model.get("UIState") !== DocumentUIState.Annotate && (
                        <>
                            <div className={leftMenuStateCss}>
                                <div className="ws-scrollable-embedded hide-overflow">
                                    <DocumentTreeControl
                                        DocumentClasses={this.state.Model.get("DocumentClasses").toList()}
                                        NonApprovedDocumentCount={this.state.Model.get("DocumentNonApprovedCountForPatient")}
                                        selectedView={this.state.Model.get("SelectedView")}
                                        isDeactivatedAccountEnabled={false}
                                        isUsedInGlobalDocumentTab={false}
                                    />
                                </div>
                            </div>
                            <div className={leftMenuControlsCss}>
                                {this.state.Model.get("LeftMenuOpen") === true && (
                                    <div onClick={this.collapseLeftMenu.bind(this)} className="slide-menu-icon">
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                )}
                                {this.state.Model.get("LeftMenuOpen") === false && (
                                    <div onClick={this.expandLeftMenu.bind(this)} className="slide-menu-icon">
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {viewToReturn}
                    {noteComponent}

                    {this.state.Model.get("MulitFilePopupOpen") === true && (
                        <AttachmentsTooltipControl
                            Attachements={this.state.Model.get("CurrentAttachements").toArray()}
                            leftPosition={this.state.Model.get("LeftPosition")}
                            topPosition={this.state.Model.get("TopPosition")}
                        />
                    )}
                </div>
            </div>
        );
    }

    private getDocumentIndex(): number {
        return this.state.Model.get("DocumentsTableData")
            .toArray()
            .findIndex(item => item.Model.Model.Id === this.state.Model.get("SelectedDocumentId"));
    }

    private getDocumentCount(): number {
        return this.state.Model.get("DocumentsTableData").toArray().length;
    }

    private ResetDocumentModel(patientContactId: string): void {
        documentsStore.ImportDocumentsModel(patientStore.PatientDocuments[patientContactId]);
        documentsStore.InitializeForGlobalDocuments(true, true);
        this.changeListener();
    }

    private changeListener(): void {
        var s = new PatientDocumentsState();
        s.Model = documentsStore.DocumentsModel;
        s.ColumnManagerModel = documentsStore.ColumnManagerModel;
        this.setState(s);
    }

    private expandLeftMenu(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.ExpandLeftMenu);
    }

    private collapseLeftMenu(): void {
        LocalEvent.Dispatch(LocalOperations.Documents.CollapseLeftMenu);
    }

    private onDocumentNavigation(navigation: DocumentNavigation): void {
        const documentList = this.state.Model.get("DocumentsTableData").toArray();
        const documentIndex = this.getDocumentIndex();
        const nextIndex = navigation === DocumentNavigation.Forward ? documentIndex + 1 : documentIndex - 1;

        if (nextIndex < 0 || nextIndex > documentList.length) {
            throw new Error("Navigation number is out of range for the document's list");
        }

        const nextDocument = documentList[nextIndex];
        this.sortDocumentAttachments(nextDocument.Model.Model);

        sessionStore.dispatch(LocalOperations.Documents.DisplayDocument, {
            docId: nextDocument.Model.Model.Id,
            annotationView: true
        });
    }

    private sortDocumentAttachments(medicalDocumentItem: any): void {
        if (cacheStore.ClinicSettings.EnableOntarioMdHealthReportManager &&
            medicalDocumentItem.Source === "HRM") {
            medicalDocumentItem.DocumentPathList = AttachmentSortingHelper.sortAttachmentsByHRMFilenameDate(
                medicalDocumentItem.DocumentPathList
            );
        }
    }
}

export { PatientDocuments };
